<template>
  <div class="payment-options">
    <div class="payment-options__title">Payment Method</div>
    <label v-for="option in paymentOptions" :key="option.key" class="payment-options__option">
      <input
        :checked="selectedPaymentOptionKey === option.key"
        :value="option.key"
        type="radio"
        name="payment-option"
        @change="emit('select', option.key)"
      />
      <span class="payment-options__content">
        <span class="payment-options__label">{{ option.label }}</span>
        <span v-if="option.description" class="payment-options__description">{{ option.description }}</span>
      </span>
      <button
        v-if="option.savedPaymentMethodId"
        class="payment-options__delete"
        type="button"
        title="Remove saved payment method"
        :disabled="deletingId === option.savedPaymentMethodId"
        @click.prevent="deleteWithConfirmAsync(option)"
      >
        <font-awesome-icon icon="fa-solid fa-trash" />
      </button>
    </label>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { EnumSensePassPaymentMethod } from "@/Domain/EnumSensePassPaymentMethod";

export type PaymentOptionSubmitMode = "sensepass-saved" | "sensepass-new";

export interface PaymentOption {
  key: string;
  type: EnumSensePassPaymentMethod;
  label: string;
  description?: string;
  submitMode: PaymentOptionSubmitMode;
  savedPaymentMethodId?: number;
}

defineProps<{
  paymentOptions: PaymentOption[];
  selectedPaymentOptionKey: string;
}>();

const emit = defineEmits<{
  (e: "select", key: string): void;
  (e: "deleted", savedPaymentMethodId: number): void;
}>();

const main = useMainStore();
const notificationStore = useNotificationStore();
const deletingId = ref<number | null>(null);

const deleteWithConfirmAsync = async function (option: PaymentOption) {
  if (!option.savedPaymentMethodId) return;
  if (!confirm(`Remove "${option.label}" from your saved payment methods?`)) return;

  const id = option.savedPaymentMethodId;
  deletingId.value = id;
  await handleResponsePromiseAsync(
    api.get("DeleteSensePassPaymentMethod", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      paymentMethodId: id,
    }),
    (success) => {
      if (success) {
        emit("deleted", id);
        notificationStore.notify({
          message: "Payment method removed.",
          type: NotificationTypeEnum.success,
          duration: 4000,
        });
      } else {
        notificationStore.notify({
          message: "Failed to remove payment method. Please try again.",
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
      }
    },
    "PaymentOptions.vue",
    "deleteWithConfirmAsync",
    () => {
      notificationStore.notify({
        message: "Failed to remove payment method. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
  deletingId.value = null;
};
</script>

<style scoped lang="scss">
.payment-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f8fafc;

  &__title {
    font-weight: 600;
  }

  &__option {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  &__label {
    font-weight: 500;
  }

  &__description {
    color: #475569;
    font-size: 0.9rem;
  }

  &__delete {
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    font-size: 0.75rem;
    line-height: 1;
    padding: 0 4px;
    align-self: center;

    &:hover {
      color: #ef4444;
    }
  }
}
</style>
